import React, { useMemo, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CounterBlock,
  Wheel,
  WheelLabel,
  WheelSublabel,
} from '@m12s/component-library';
import _startCase from 'lodash/startCase';
import _capitalize from 'lodash/capitalize';
import { useSelector } from 'react-redux';

import {
  diff,
  getUnitDurationFromString,
  dateUTC,
  millis,
} from 'lib/utils/date';
import {
  SETUP_ELAPSED_DURATION_TICKER_PROPS,
  formatExpectedSetupDurationForDisplay,
} from 'lib/utils/setupDurationDisplay';
import { PartsMadeChart } from 'components/charts/PartsMadeChart';
import { DurationTicker } from 'components/DurationTicker';
import { Display, InSetupDisplayText } from 'components/Text';
import { TickerContext } from 'components/TickerProvider';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { getAdjustedSetupDurationDates } from 'lib/selectors/getAdjustedSetupDurationDates';
import { getLatestSetupActivityEndAt } from 'lib/selectors/getLatestSetupActivityEndAt';
import { getIsPaused } from 'lib/selectors/getIsPaused';
import {
  PauseDetailsToggle,
  PauseDetailsOverlayContainer,
} from 'components/PauseDetails';
import { getArePartCountBucketsLoading } from 'lib/selectors/getPartCountBuckets';
import { WheelContentOpRunOverride } from 'components/TabsCommon/WheelContentOpRunOverride';
import {
  ChartLabel,
  WheelCounterPadding,
  WheelLayout,
  WheelChartPadding,
} from 'components/WheelLayout';
import PausedWheelContent from 'components/TabsCommon/PausedWheelContent';
import { getShiftStart } from 'lib/selectors/getShiftStart';
import { getSetupModeSpansShift } from 'lib/selectors/getSetupModeSpansShift';
import { getSetupRejectCount } from 'lib/selectors/getSetupParts';
import { getIsJobLaborTrackingEnabled } from 'lib/selectors/getIsJobLaborTrackingEnabled';
import { getShouldUseOpRunOverrideScope } from 'lib/selectors/getShouldUseOpRunOverrideScope';
import { getIsMachineManualPartCount } from 'lib/selectors/getIsMachineManualPartCount';
import { ManualPartCountWheel } from 'components/MachineStatus/ManualPartCountWheel';
import { AdjustSetupPartsModal } from './AdjustSetupPartsModal';
import { SetupPartsDisplay, ShiftSetupPartsDisplay } from './SetupPartsDisplay';

const getWheelAppearance = ({
  expectedSetupDurationMs,
  shouldUseOpRunOverrideScope,
  isOvertime,
}) => {
  // No expected duration cases
  if (!expectedSetupDurationMs) {
    return shouldUseOpRunOverrideScope ? 'setup' : 'empty';
  }

  // Has expected duration cases
  return isOvertime ? 'failure' : 'setup';
};

const WheelContentStandard = ({ isOvertime }) => {
  const { t } = useTranslation();
  const latestActivitySet = useSelector(getLatestActivitySet);
  const { expectedSetupDuration } = latestActivitySet;
  const expectedSetupDurationMs = getUnitDurationFromString(
    expectedSetupDuration,
    'milliseconds'
  );
  const { expectedSetupEndDate } = useSelector(getAdjustedSetupDurationDates);
  const isPaused = useSelector(getIsPaused);
  const setupActivityEndAt = useSelector(getLatestSetupActivityEndAt);
  const setupRejectCount = useSelector(getSetupRejectCount);
  const isJobLaborTrackingEnabled = useSelector(getIsJobLaborTrackingEnabled);

  if (isPaused) {
    return (
      <PausedWheelContent expectedSetupDurationMs={expectedSetupDurationMs} />
    );
  }

  return (
    <>
      {!!expectedSetupDurationMs && expectedSetupEndDate && (
        <>
          <WheelLabel>
            <DurationTicker
              reverse={!isOvertime}
              startDate={expectedSetupEndDate}
              endDateOverride={setupActivityEndAt}
              {...SETUP_ELAPSED_DURATION_TICKER_PROPS}
            />
          </WheelLabel>
          <WheelSublabel>
            {_startCase(t(isOvertime ? 'overtime' : 'remaining'))}
          </WheelSublabel>
        </>
      )}
      {isJobLaborTrackingEnabled ? (
        !expectedSetupDurationMs && <InSetupDisplayText />
      ) : (
        <>
          <WheelLabel secondary>{setupRejectCount || '-'}</WheelLabel>
          <WheelSublabel>{_capitalize(t('rejects'))}</WheelSublabel>
        </>
      )}
    </>
  );
};

const MachineSetup = () => {
  const { t } = useTranslation();
  const nowMs = useContext(TickerContext);
  const isMachineManualPartCount = useSelector(getIsMachineManualPartCount);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const { adjustedSetupStartDate, expectedSetupEndDate } = useSelector(
    getAdjustedSetupDurationDates
  );
  const shouldUseOpRunOverrideScope = useSelector(
    getShouldUseOpRunOverrideScope
  );
  const arePartCountBucketsLoading = useSelector(getArePartCountBucketsLoading);
  const isPaused = useSelector(getIsPaused);
  const shiftStart = useSelector(getShiftStart);
  const setupModeSpansShift = useSelector(getSetupModeSpansShift);
  const { expectedSetupDuration } = latestActivitySet;
  const expectedSetupDurationMs = getUnitDurationFromString(
    expectedSetupDuration,
    'milliseconds'
  );
  const setupActivityEndAt = useSelector(getLatestSetupActivityEndAt);
  const endMs = setupActivityEndAt ? millis(setupActivityEndAt) : nowMs;
  const isOvertime = useMemo(() => {
    if (!expectedSetupEndDate || !endMs) return false;
    return dateUTC(endMs).isAfter(expectedSetupEndDate);
    /* when unpaused, this updates to 'now' every second */
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [expectedSetupEndDate, endMs]);
  const wheelAppearance = getWheelAppearance({
    expectedSetupDurationMs,
    shouldUseOpRunOverrideScope,
    isOvertime,
  });

  const progressBarPercentage = useMemo(() => {
    if (!adjustedSetupStartDate) {
      return 1;
    }
    const elapsedMs = Math.max(
      diff(endMs, adjustedSetupStartDate, 'milliseconds'),
      0
    );
    return Math.round((elapsedMs / expectedSetupDurationMs) * 100) || 1; // default so bar never starts empty
    /* when unpaused, this updates to 'now' every second */
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [endMs, adjustedSetupStartDate, expectedSetupDurationMs]);

  const [adjustPartsModalOpen, setAdjustPartsModalOpen] = useState(false);
  const calculateValue = (item, getItemStyle) => {
    return {
      value: [item.time, item.sum],
      itemStyle: getItemStyle(item),
    };
  };
  return (
    <PauseDetailsOverlayContainer>
      <WheelLayout
        isPaused={isPaused}
        id="setup-part-count-page"
        topLeft={
          <WheelCounterPadding top left>
            {setupModeSpansShift ? (
              <ShiftSetupPartsDisplay
                setAdjustPartsModalOpen={setAdjustPartsModalOpen}
                t={t}
                shiftStart={shiftStart}
              />
            ) : (
              <SetupPartsDisplay
                setAdjustPartsModalOpen={setAdjustPartsModalOpen}
                t={t}
                setupModeSpansShift={setupModeSpansShift}
              />
            )}
          </WheelCounterPadding>
        }
        topRight={
          <WheelChartPadding>
            <ChartLabel
              tooltip={t(
                'This area displays the number of Parts that have been produced by this Machine at specific times during the current shift.'
              )}
              loading={arePartCountBucketsLoading}
            >
              {_startCase(t('parts made'))}
            </ChartLabel>
            <PartsMadeChart calculateValue={calculateValue} />
          </WheelChartPadding>
        }
        bottomLeft={
          <WheelCounterPadding bottom left>
            <CounterBlock
              label={t('Total Elapsed Setup')}
              value={
                <Display>
                  {adjustedSetupStartDate && (
                    <DurationTicker
                      startDate={adjustedSetupStartDate}
                      endDateOverride={setupActivityEndAt}
                      {...SETUP_ELAPSED_DURATION_TICKER_PROPS}
                    />
                  )}
                </Display>
              }
            />
          </WheelCounterPadding>
        }
        bottomRight={
          <WheelCounterPadding bottom right>
            <CounterBlock
              align="right"
              label={t('Total Expected Setup')}
              value={
                <Display>
                  {formatExpectedSetupDurationForDisplay(
                    expectedSetupDuration
                  ) || _startCase(t('none'))}
                </Display>
              }
            />
          </WheelCounterPadding>
        }
        wheel={
          !isMachineManualPartCount ? (
            <div
              id="setup-progress-wheel-container"
              style={{
                opacity: isPaused ? 0.5 : 1,
              }}
            >
              <Wheel
                id="setup-progress-wheel"
                shape="rounded"
                value={
                  !!expectedSetupDurationMs &&
                  Math.min(progressBarPercentage, 100)
                }
                appearance={wheelAppearance}
                fillMode="full"
                withMarkers
                Contents={
                  shouldUseOpRunOverrideScope ? (
                    <WheelContentOpRunOverride
                      appearance={wheelAppearance}
                      fillMode="full"
                    />
                  ) : (
                    <WheelContentStandard />
                  )
                }
              />
            </div>
          ) : (
            <div>
              <ManualPartCountWheel />
            </div>
          )
        }
      />
      <PauseDetailsToggle />
      <AdjustSetupPartsModal
        open={adjustPartsModalOpen}
        onClose={() => {
          setAdjustPartsModalOpen(false);
        }}
      />
    </PauseDetailsOverlayContainer>
  );
};

export { MachineSetup };
